import { BookingReason } from '@app/domain/BookingReason'
import { OrdinalDatePipe } from '@app/features/shared/pipes/ordinal-date.pipe'
import { Pipe, PipeTransform } from '@angular/core'

@Pipe({
    name: 'reasonDateRanges',
})
export class ReasonDateRangesPipe implements PipeTransform {

    private static readonly MAX_DISPLAY_RANGES = 2
    private static readonly ALL_DATES_TEXT = 'All dates'

    constructor(private ordinalDate: OrdinalDatePipe) {}

    transform(reason: BookingReason): string | null {
        if (!reason?.dateRangeFilters?.length) {
            return ReasonDateRangesPipe.ALL_DATES_TEXT
        }

        const sortedRanges = this.sortRangesByStartDate(reason.dateRangeFilters)
        const groupedRanges = this.groupRangesByMonthDayPattern(sortedRanges)
        const formattedRanges = this.formatGroupedRanges(groupedRanges)
        
        return this.buildFinalOutput(formattedRanges, sortedRanges.length)
    }

    private sortRangesByStartDate(ranges: BookingReason['dateRangeFilters']): typeof ranges {
        return [...ranges].sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
    }

    private groupRangesByMonthDayPattern(ranges: typeof BookingReason.prototype.dateRangeFilters) {
        type DateRange = typeof ranges[number]
        type RangeGroup = {
            items: DateRange[]
            years: number[]
            firstStartTime: number
            hasCrossYearRange: boolean
        }

        const groups = new Map<string, RangeGroup>()

        for (const range of ranges) {
            const groupKey = this.createMonthDayGroupKey(range)
            const isCrossYearRange = this.isCrossYearRange(range)
            
            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    items: [],
                    years: [],
                    firstStartTime: range.startDate.getTime(),
                    hasCrossYearRange: false
                })
            }

            const group = groups.get(groupKey)!
            group.items.push(range)
            group.years.push(range.startDate.getFullYear(), range.endDate.getFullYear())
            group.hasCrossYearRange = group.hasCrossYearRange || isCrossYearRange
        }

        return this.sortGroupsByFirstStart(groups)
    }

    private createMonthDayGroupKey(range: { startDate: Date; endDate: Date }): string {
        const startKey = `${range.startDate.getMonth() + 1}-${range.startDate.getDate()}`
        const endKey = `${range.endDate.getMonth() + 1}-${range.endDate.getDate()}`
        return `${startKey}__${endKey}`
    }

    private isCrossYearRange(range: { startDate: Date; endDate: Date }): boolean {
        return range.startDate.getFullYear() !== range.endDate.getFullYear()
    }

    private sortGroupsByFirstStart(groups: Map<string, any>): Array<[string, any]> {
        return Array.from(groups.entries())
            .sort((a, b) => a[1].firstStartTime - b[1].firstStartTime)
    }

    private formatGroupedRanges(groupedRanges: Array<[string, any]>): { text: string; count: number }[] {
        const results: { text: string; count: number }[] = []
        
        for (const [, group] of groupedRanges) {
            if (results.length >= ReasonDateRangesPipe.MAX_DISPLAY_RANGES) {
                break
            }

            const formattedRange = this.formatRangeGroup(group)
            results.push(formattedRange)
        }

        return results
    }

    private formatRangeGroup(group: any): { text: string; count: number } {
        const hasMultipleYears = group.items.length > 1 && !group.hasCrossYearRange
        
        if (hasMultipleYears) {
            return this.formatMultiYearRange(group)
        }
        
        return this.formatSingleYearRange(group.items[0])
    }

    private formatMultiYearRange(group: any): { text: string; count: number } {
        const representativeRange = group.items[0]
        const distinctYears = this.getDistinctYears(group.years)
        const baseText = this.formatDateRangeWithoutYear(representativeRange)
        
        return {
            text: `${baseText} (${distinctYears.join(', ')})`,
            count: group.items.length
        }
    }

    private formatSingleYearRange(range: any): { text: string; count: number } {
        return {
            text: this.formatDateRangeWithYear(range),
            count: 1
        }
    }

    private formatDateRangeWithoutYear(range: { startDate: Date; endDate: Date }): string {
        const isSameMonth = this.areSameMonthAndYear(range.startDate, range.endDate)
        
        if (isSameMonth) {
            return this.formatSameMonthRange(range.startDate, range.endDate, false)
        }
        
        return this.formatDifferentMonthRange(range.startDate, range.endDate, false)
    }

    private formatDateRangeWithYear(range: { startDate: Date; endDate: Date }): string {
        const isSameYear = range.startDate.getFullYear() === range.endDate.getFullYear()
        const isSameMonth = isSameYear && this.areSameMonth(range.startDate, range.endDate)
        
        if (isSameMonth) {
            return this.formatSameMonthRange(range.startDate, range.endDate, true)
        }
        
        if (isSameYear) {
            return this.formatDifferentMonthRange(range.startDate, range.endDate, true)
        }
        
        return this.formatCrossYearRange(range.startDate, range.endDate)
    }

    private formatSameMonthRange(startDate: Date, endDate: Date, includeYear: boolean): string {
        const startDay = this.formatWithOrdinal(startDate, 'do')
        const month = this.formatWithOrdinal(startDate, 'MMM')
        const endFormat = includeYear ? 'do MMM yyyy' : 'do MMM'
        const endDayMonthYear = this.formatWithOrdinal(endDate, endFormat)
        
        return `${startDay} ${month} - ${endDayMonthYear}`.replace('  -', ' -')
    }

    private formatDifferentMonthRange(startDate: Date, endDate: Date, includeYear: boolean): string {
        const startFormat = 'do MMM'
        const endFormat = includeYear ? 'do MMM yyyy' : 'do MMM'
        
        const start = this.formatWithOrdinal(startDate, startFormat)
        const end = this.formatWithOrdinal(endDate, endFormat)
        
        return `${start} - ${end}`
    }

    private formatCrossYearRange(startDate: Date, endDate: Date): string {
        const start = this.formatWithOrdinal(startDate, 'do MMM yyyy')
        const end = this.formatWithOrdinal(endDate, 'do MMM yyyy')
        
        return `${start} - ${end}`
    }

    private areSameMonth(date1: Date, date2: Date): boolean {
        return date1.getMonth() === date2.getMonth()
    }

    private areSameMonthAndYear(date1: Date, date2: Date): boolean {
        return this.areSameMonth(date1, date2) && 
               date1.getFullYear() === date2.getFullYear()
    }

    private getDistinctYears(years: number[]): number[] {
        return Array.from(new Set(years)).sort((a, b) => a - b)
    }

    private formatWithOrdinal(date: Date, format: string): string {
        return this.ordinalDate.transform(date, format, 'day-of-month') ?? ''
    }

    private buildFinalOutput(formattedRanges: { text: string; count: number }[], totalRanges: number): string {
        const displayedTexts = formattedRanges.map(r => r.text)
        const displayedCount = formattedRanges.reduce((sum, r) => sum + r.count, 0)
        const omittedCount = totalRanges - displayedCount
        
        let output = displayedTexts.join('\n')
        
        if (omittedCount > 0) {
            output += `\nand ${omittedCount} more`
        }
        
        return output
    }
}
